import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, TextField, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import { toast } from 'react-toastify';
import { IdeaTextCopyButton } from './campaignImagePromptUI';
import { auth } from '../../firebase';
import { apiUrl } from '../../utils/apiBase';

const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const safeArray = (value) => (Array.isArray(value) ? value : []);

const sectionSx = {
  border: '1px solid rgba(151,113,39,0.18)',
  borderRadius: 1.8,
  p: 1.8,
  bgcolor: 'rgba(255,255,255,0.74)'
};

const titleSx = {
  color: '#7a4d0b',
  fontSize: '1.15rem',
  fontWeight: 900,
  mb: 1
};

const textSx = {
  color: '#2f2516',
  fontSize: '1.02rem',
  lineHeight: 1.55
};

const getCustomIdeaChatUrl = () => apiUrl('/api/campaign-custom-idea-chat');

const getCampaignAiHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  const sharedSecret = safeString(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET).trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    // Token eklenemezse backend env ayarına göre isteği reddedebilir.
  }
  return headers;
};

const CUSTOM_IDEA_CHAT_WELCOME = {
  role: 'assistant',
  content:
    'Merhaba, kampanyaya eklenecek yeni fikri birlikte netleştirelim. Aklınızdaki yönü kısaca yazın; gerekirse netleştirmek için soru sorup öneri sunacağım.'
};

const requestCustomIdeaChat = async ({ campaign, selectedIdeas, messages }) => {
  const url = getCustomIdeaChatUrl();
  if (!url) {
    throw new Error('AI servisi canlı ortama bağlı değil. Geçerli bir sunucu URL’i tanımlanmalı.');
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: await getCampaignAiHeaders(),
    body: JSON.stringify({
      campaignName: safeString(campaign?.name, 'Yeni Kampanya'),
      goal: safeString(campaign?.goal, safeString(campaign?.campaignDetails?.goal, 'kampanya etkisini artırmak')),
      selectedIdeas,
      messages
    })
  });
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) {
    throw new Error('AI servisi şu an canlıda aktif değil. Teknik ekip endpoint bağlantısını tamamlamalı.');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(safeString(data?.error, 'Sohbet yanıtı alınamadı'));
  }
  const data = await response.json();
  return safeString(data?.reply).trim();
};

const buildAiGoalFallback = (campaign) => {
  const name = safeString(campaign?.name, 'Yeni Kampanya');
  const goal = safeString(campaign?.goal, 'kampanya hedefini müşteriye net anlatmak');
  return `${name} için kampanya hedefi; ${goal.toLocaleLowerCase('tr-TR')} odağını mağaza, personel ve dijital iletişimde aynı çizgide uygulamak, müşteri ilgisini artırmak ve satış dönüşümünü güçlendirmektir.`;
};

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const campaignFileSlug = (name) => {
  const t = safeString(name, 'kampanya')
    .replace(/[^\w\-\sğüşıöçĞÜŞİÖÇ]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 48);
  return t || 'kampanya';
};

/**
 * @returns {{ plainText: string, printHtml: string, fileSlug: string, panelSections: Array<{key:string, title:string, lines?: string[], body?: string}> }}
 */
const buildCampaignDetailSnapshot = (
  campaign,
  {
    details,
    selectedStoreIdeas,
    selectedStaffIdeas,
    selectedWebsiteIdeas,
    selectedInstagramIdeas,
    customIdeas,
    submittedIdeas,
    selectedSubmittedIdeaIds
  }
) => {
  const name = safeString(campaign?.name, 'Yeni Kampanya');
  const start = safeString(campaign?.start, '-');
  const end = safeString(campaign?.end, '-');
  const goal = safeString(details?.goal, safeString(campaign?.goal, 'Kampanya hedefi belirtilmedi.'));
  const aiGoal = safeString(details?.aiGoalText, buildAiGoalFallback(campaign));

  const panelSections = [
    {
      key: 'summary',
      title: 'Kampanya özeti',
      lines: [
        `Kampanya adı: ${name}`,
        `Tarih: ${start} - ${end}`,
        `Yönetici hedefi: ${goal}`
      ]
    },
    { key: 'ai', title: 'AI kampanya hedefi', body: aiGoal },
    {
      key: 'store',
      title: 'Mağaza / Vitrin / Banko — işaretlenen fikirler',
      list: selectedStoreIdeas,
      empty: 'Bu kanalda henüz işaretli fikir yok.'
    },
    {
      key: 'staff',
      title: 'Personel — işaretlenen fikirler',
      list: selectedStaffIdeas,
      empty: 'Bu kanalda henüz işaretli fikir yok.'
    },
    {
      key: 'web',
      title: 'Web sitesi — işaretlenen fikirler',
      list: selectedWebsiteIdeas,
      empty: 'Bu kanalda henüz işaretli fikir yok.'
    },
    {
      key: 'ig',
      title: 'Instagram — işaretlenen fikirler',
      list: selectedInstagramIdeas,
      empty: 'Bu kanalda henüz işaretli fikir yok.'
    },
    {
      key: 'custom',
      title: 'Yeni fikir (sohbet ile eklenenler)',
      list: customIdeas,
      empty: 'Henüz ek fikir yok.'
    }
  ];

  const branchApproved = safeArray(submittedIdeas)
    .map((idea, index) => {
      const id = safeString(idea?.id, `${index}`);
      if (!selectedSubmittedIdeaIds.includes(id)) return null;
      const branch = safeString(idea?.branchLabel, 'Şube');
      const when = safeString(idea?.createdAt, '-');
      const text = safeString(idea?.text, '-');
      return `${branch} — ${when}: ${text}`;
    })
    .filter(Boolean);
  panelSections.push({
    key: 'branch',
    title: 'Şubelerden onaylanan fikirler',
    list: branchApproved,
    empty: 'Onaylanan şube fikri yok (Sunulan fikirler sekmesinden işaretleyin).'
  });

  const plainLines = [];
  plainLines.push('KAMPANYA DETAY RAPORU');
  plainLines.push('─'.repeat(44));
  plainLines.push(`Kampanya: ${name}`);
  plainLines.push(`Tarih: ${start} - ${end}`);
  plainLines.push(`Yönetici hedefi: ${goal}`);
  plainLines.push('');
  plainLines.push('AI KAMPANYA HEDEFİ');
  plainLines.push('─'.repeat(44));
  plainLines.push(aiGoal);
  plainLines.push('');

  const addPlainList = (title, list, empty) => {
    plainLines.push(title);
    plainLines.push('─'.repeat(Math.min(title.length, 44)));
    if (!list || !list.length) {
      plainLines.push(empty);
    } else {
      list.forEach((line, i) => plainLines.push(`${i + 1}. ${line}`));
    }
    plainLines.push('');
  };

  addPlainList('MAĞAZA / VİTRİN / BANKO', selectedStoreIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  addPlainList('PERSONEL', selectedStaffIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  addPlainList('WEB SİTESİ', selectedWebsiteIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  addPlainList('INSTAGRAM', selectedInstagramIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  addPlainList('YENİ FİKİR (SOHBET)', customIdeas, 'Henüz ek fikir yok.');
  addPlainList('ŞUBELERDEN ONAYLANAN FİKİRLER', branchApproved, 'Onaylanan şube fikri yok.');

  const plainText = plainLines.join('\n').trim();

  let h = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(name)}</title>
<style>
body{font-family:system-ui,Segoe UI,sans-serif;padding:28px;max-width:720px;margin:0 auto;color:#1f1a14;background:#fff;}
h1{font-size:1.35rem;border-bottom:2px solid #9a6a1f;padding-bottom:10px;margin:0 0 6px;}
.meta{color:#5c4d3a;font-size:0.95rem;margin:0 0 22px;}
h2{font-size:1.05rem;color:#7a4d0b;margin:1.15em 0 0.5em;}
ul{margin:0.25em 0 0.9em 1.15em;padding:0;}
li{margin:0.32em 0;}
.muted{color:#6b5b4a;font-style:italic;}
.body-text{white-space:pre-wrap;line-height:1.55;}
</style></head><body>`;
  h += `<h1>${escapeHtml(name)}</h1><p class="meta">${escapeHtml(start)} — ${escapeHtml(end)}</p>`;
  h += '<h2>Kampanya özeti</h2><ul>';
  h += `<li><strong>Ad:</strong> ${escapeHtml(name)}</li>`;
  h += `<li><strong>Tarih:</strong> ${escapeHtml(start)} — ${escapeHtml(end)}</li>`;
  h += `<li><strong>Yönetici hedefi:</strong> ${escapeHtml(goal)}</li></ul>`;
  h += '<h2>AI kampanya hedefi</h2>';
  h += `<div class="body-text">${escapeHtml(aiGoal)}</div>`;
  const htmlList = (title, list, empty) => {
    h += `<h2>${escapeHtml(title)}</h2>`;
    if (!list || !list.length) h += `<p class="muted">${escapeHtml(empty)}</p>`;
    else {
      h += '<ul>';
      list.forEach((x) => {
        h += `<li>${escapeHtml(x)}</li>`;
      });
      h += '</ul>';
    }
  };
  htmlList('Mağaza / Vitrin / Banko', selectedStoreIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  htmlList('Personel', selectedStaffIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  htmlList('Web sitesi', selectedWebsiteIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  htmlList('Instagram', selectedInstagramIdeas, 'Bu kanalda henüz işaretli fikir yok.');
  htmlList('Yeni fikir (sohbet)', customIdeas, 'Henüz ek fikir yok.');
  htmlList('Şubelerden onaylanan fikirler', branchApproved, 'Onaylanan şube fikri yok.');
  h += '<p class="meta" style="margin-top:2em">UKBA Kasa — Kampanya detay raporu</p></body></html>';

  return { plainText, printHtml: h, fileSlug: campaignFileSlug(name), panelSections };
};

const ensureTwentyStoreIdeas = (ideas, campaignName, goal) => {
  const name = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'müşteri ilgisini artırmak');
  const extraIdeas = [
    `${name} için mağaza dış camına sade ve okunur kampanya duyurusu yerleştir.`,
    'Mağaza girişine küçük karşılama standı koy; kampanyalı ürünleri ilk temas noktasında göster.',
    'Vitrinin alt bölümüne küçük hediye paketi örnekleri yerleştir; hediye algısını güçlendir.',
    'Bankoda kampanya ürünleri için ayrı kadife tepsi hazırla; ürünler dağınık görünmesin.',
    'Kasa önüne küçük sürpriz/hediye sepeti koy; alışveriş sonrası müşteriye verilecek seçenekler hazır dursun.',
    'Mağaza içinde yönlendirici küçük kartlar kullan; “hediye önerileri”, “çok tercih edilenler” gibi kısa başlıklar olsun.',
    'Vitrindeki ürünleri fiyat seviyesine göre grupla; müşteri dışarıdan bakınca seçenekleri hızlı anlasın.',
    'Mağaza dışından görünen bir ürünü kampanyanın yıldız ürünü olarak tek başına öne çıkar.',
    'Banko arkasında kampanya kutusu/paketleme alanı oluştur; paketler düzenli ve premium görünsün.',
    'Deneme alanına küçük ayna, temiz tepsi ve kombin kartı koy; müşteri ürünü üzerinde kolay hayal etsin.',
    'Girişe yakın alanda “bugünün öne çıkan ürünü” için tek ürünlük mini teşhir oluştur.',
    'Vitrinde yüksekliği farklı stantlar kullan; kampanya ürünleri düz dizilmiş gibi görünmesin.',
    'Kampanya ürünlerinin yanına küçük bakım bezi veya mini hediye kartı örneği koy.',
    'Mağaza içindeki ışığı kampanyalı ürünlere yönlendir; karanlık kalan ürünleri vitrin dışına çıkar.',
    'Bankoda hızlı paketlenmiş hazır hediye kutuları bulundur; yoğun saatte beklemeyi azalt.',
    `${safeGoal} temasını mağazada tek cümlelik masa üstü kartla görünür yap.`,
    'Mağaza çıkışında küçük teşekkür kartı ve bakım önerisi kartı verilecek alan hazırla.',
    'Kampanyalı ürünleri “günlük kullanım”, “hediye”, “özel gün” olarak fiziksel alanlara ayır.',
    'Vitrinde müşterinin dokunmak isteyeceği en parlak/parlayan ürünleri ön sıraya al.',
    'Gün sonunda vitrinde az ilgi gören ürünü değiştirip bankoda daha hızlı gösterilecek alana taşı.'
  ];
  return [...ideas, ...extraIdeas].filter((item, idx, arr) => item && arr.indexOf(item) === idx).slice(0, 20);
};

const buildStoreIdeasFallback = (campaign) => {
  const name = safeString(campaign?.name, 'Kampanya');
  const goal = safeString(campaign?.goal, 'müşteri ilgisini artırmak');
  const savedIdeas = safeArray(campaign?.storeIdeas).filter(Boolean);
  if (savedIdeas.length) return ensureTwentyStoreIdeas(savedIdeas, name, goal);

  const text = `${name} ${goal}`.toLocaleLowerCase('tr-TR');

  if (text.includes('23 nisan') || text.includes('çocuk') || text.includes('cocuk')) {
    return ensureTwentyStoreIdeas([
      'Çocuklara özel takı vitrini hazırla; küçük küpe, bileklik, kolye ve renkli figürlü ürünleri ayrı sergile.',
      'Vitrine balon, kırmızı-beyaz süsleme ve çocuk bayramı temalı küçük bayrak detayları ekle.',
      'Çocuk müşterilere küçük sürpriz alanı oluştur; şeker, mini bileklik veya renkli paket kartı hazır bulunsun.',
      'Ailelere özel “çocuğa hediye” öneri köşesi kur; fiyat ve ürün seçeneklerini net ayır.',
      'Mağaza içinde çocuklarla fotoğraf çekilebilecek küçük hatıra köşesi oluştur.',
      'Bankoda “23 Nisan hediye önerileri” tepsisi hazırla; ürünler fiyat aralığına göre kolayca seçilebilsin.',
      'Çocuk ürünlerinin paketlerini renkli kurdele veya özel küçük etiketle farklılaştır.',
      'Çocuklar için mağaza çıkışında küçük sürpriz kutusu hazırla; şeker, mini bileklik veya renkli kart ekle.',
      'Vitrinde anne-baba-çocuk hediye kombinleri oluştur; aile alışverişini kolaylaştır.',
      'Gün sonunda en çok ilgi gören çocuk ürünlerini ertesi gün girişe yakın alana taşı.'
    ], name, goal);
  }

  if (text.includes('anne')) {
    return ensureTwentyStoreIdeas([
      '“Anneye özel hediye” vitrini kur; zarif kolye, bileklik ve küpeleri yumuşak ışıkla öne çıkar.',
      'Bankoda üç bütçeli hediye tepsisi hazırla: ekonomik, özel ve premium seçenekler.',
      'Hediye paketi alanını görünür yap; kurdele, not kartı ve sertifika hazır beklesin.',
      'Vitrine anne-çocuk duygusunu anlatan küçük mesaj kartları yerleştir.',
      '“Sade anne”, “gösterişli anne” ve “klasik anne” olarak üç küçük hediye vitrini oluştur.',
      'Girişte “son dakika anne hediyesi” köşesi oluştur; hızlı karar verdiren ürünleri sergile.',
      'Set kombinleri hazırla; kolye-küpe veya bileklik-yüzük eşleştirmelerini ayrı göster.',
      'Bankoda paketlenmiş örnek hediye sunumu bulundur; müşteri alacağı paketi zihninde görsün.',
      'Vitrinde kalp, çiçek ve zarif taşlı ürünleri tek tema altında topla.',
      'Satış sonrası müşteriye küçük bakım/koruma önerisi kartı ver; hediyenin değer algısını artır.'
    ], name, goal);
  }

  if (text.includes('sevgili') || text.includes('14 şubat') || text.includes('14 subat')) {
    return ensureTwentyStoreIdeas([
      'Çiftlere özel alyans, kolye ve bileklik vitrini kur; ikili ürünleri yan yana sergile.',
      'Bankoda “sevgiliye hızlı hediye” tepsisi hazırla; 5 net ürün önerisi bulunsun.',
      'Vitrine kırmızı detay, kalp obje ve sıcak ışıkla romantik bir alan oluştur.',
      'Hediye paketlerinde kırmızı kurdele ve küçük not kartı seçeneğini görünür yap.',
      'Mağaza girişinde günlük kullanım ve özel gün hediyesi ürünlerini iki ayrı alanda sergile.',
      'İsim/harf/kalp temalı ürünleri ayrı bir mini stantta topla.',
      'Çift kombinleri hazırla; kadın-erkek uyumlu bileklik veya kolye seçeneklerini göster.',
      'Kasa yanında son dakika hediye ürünleri için küçük premium kutu alanı kur.',
      'Vitrinde tek güçlü ürün yerine 3 farklı bütçe senaryosu oluştur.',
      'Satın alan müşteriye hediye notu yazabileceği küçük kart sun.'
    ], name, goal);
  }

  if (text.includes('düğün') || text.includes('dugun') || text.includes('nişan') || text.includes('nisan')) {
    return ensureTwentyStoreIdeas([
      'Düğün/nişan vitrini kur; alyans, set, bilezik ve takı setlerini ayrı tema halinde sergile.',
      'Bankoda gelin-damat aileleri için hızlı danışma tepsisi hazırla.',
      'Vitrine beyaz, gold ve zarif çiçek detaylarıyla özel gün atmosferi ver.',
      '“Takı merasimi için öneriler” alanı oluştur; bilezik ve gram altın seçeneklerini görünür yap.',
      'Takı hediyesi, gelin seti ve aile alışverişi ürünlerini ayrı ayrı etiketlenmiş alanlara ayır.',
      'Premium setleri spot ışık alan bölümde sergile; fiyat/ayar bilgileri net görünsün.',
      'Aile alışverişi için oturma/danışma alanını düzenli ve hazır tut.',
      'Bankoda sertifika, kutu ve hediye paket örneklerini açık şekilde göster.',
      'Vitrinde gelin seti ve günlük kullanım setini ayrılaştır; karar vermeyi kolaylaştır.',
      'Yoğun saatlerde düğün alışverişi yapan müşteriler için hızlı ürün listesi hazır bulunsun.'
    ], name, goal);
  }

  if (text.includes('bayram')) {
    return ensureTwentyStoreIdeas([
      'Bayram hediyesi vitrini kur; bileklik, kolye ve küçük altın seçeneklerini aile hediyesi temasıyla sergile.',
      'Bankoda “bayram ziyareti hediyesi” tepsisi hazırla; hızlı alınabilecek ürünleri öne çıkar.',
      'Vitrine sıcak ışık, sade çiçek ve bayram tebrik kartı detayı ekle.',
      'Çocuklara ve gençlere uygun küçük hediye ürünlerini ayrı mini stantta göster.',
      'Anne, genç kız ve çocuk için ayrı bayram hediyesi mini alanları oluştur.',
      'Hediye paketlerini önceden hazırla; yoğunlukta bekleme süresini azalt.',
      'Kasa yanında gram altın ve küçük takı önerilerini görünür tut.',
      'Aile kombinleri hazırla; anne, genç kız ve çocuk için ayrı öneri kartları oluştur.',
      'Bayram öncesi son gün için hızlı karar ürünlerini girişe yakın konumlandır.',
      'Gün içinde tükenen paket/malzeme ve ilgi gören ürünleri düzenli kontrol et.'
    ], name, goal);
  }

  if (text.includes('yılbaşı') || text.includes('yilbasi')) {
    return ensureTwentyStoreIdeas([
      'Yılbaşı hediye vitrini kur; parlak taşlı, yıldız ve şans temalı ürünleri öne çıkar.',
      'Vitrine gold, kırmızı ve ışıklı küçük dekorlarla sıcak hediye atmosferi ver.',
      'Bankoda “yılbaşı için son dakika hediye” tepsisi hazırla.',
      'Hediye paketlerinde özel yılbaşı kurdelesi ve küçük not kartı seçeneği sun.',
      'Kendine hediye ve sevdiklerine hediye ürünlerini iki ayrı vitrin grubunda göster.',
      'Şans ve yeni başlangıç temalı ürünleri ayrı köşede topla.',
      'Kasa yanında küçük bütçeli hediye ürünlerini görünür hale getir.',
      'Vitrinde tek ürün değil, 3 hediye senaryosu oluştur: sade, gösterişli, premium.',
      'Günlük en çok sorulan ürünleri bankoda hazır gösterim tepsisine al.',
      'Satış sonrası paket tesliminde kısa yeni yıl dileği kartı ekle.'
    ], name, goal);
  }

  if (text.includes('bahar') || text.includes('yaz')) {
    return ensureTwentyStoreIdeas([
      'Bahar/yaz vitrini kur; ince zincir, renkli taş, minimal bileklik ve canlı ürünleri öne çıkar.',
      'Vitrine açık ton dekor, ferah ışık ve hafif kombin hissi veren ürün grupları yerleştir.',
      'Bankoda “günlük kullanım için hafif takılar” tepsisi hazırla.',
      'Katmanlı kolye ve bileklik kombinlerini manken/stand üzerinde hazır göster.',
      'Günlük kullanım ve tatil kombinleri için iki ayrı yaz takısı alanı hazırla.',
      'Renkli taşlı ürünleri ayrı bir yaz enerjisi köşesinde sergile.',
      'Kasa yanında uygun fiyatlı küçük yaz hediyesi seçeneklerini göster.',
      'Vitrinde beyaz, mavi ve gold tonlarla ferah bir tema oluştur.',
      'Mağaza içinde yaz temalı küçük deneme/ayna köşesi hazırla; kombinler burada sergilensin.',
      'Gün sonunda en çok denenen yaz ürünlerini ertesi gün banko öneri alanına taşı.'
    ], name, goal);
  }

  return ensureTwentyStoreIdeas([
    `${name} için mağaza girişinde net bir kampanya köşesi kur; müşteri içeri girince ilk bunu görsün.`,
    `Bankoda ${goal} temasını anlatan 5 ürünlük hızlı öneri tepsisi hazırla.`,
    'Vitrinde kampanyanın en güçlü 3 ürününü ayrı yükselticilerle öne çıkar.',
    'Kasa yanında karar vermeyi hızlandıran küçük hediye/öneri ürünleri bulundur.',
    'Mağaza içinde kampanya avantajını anlatan küçük masa üstü bilgi kartları kullan.',
    'Ürünleri bütçeye göre ayır: giriş, orta ve premium seçenekler net görünsün.',
    'Deneme alanına kombin önerisi kartı koy; müşteri ürünü nasıl kullanacağını görsün.',
    'Hediye paketi ve sertifika örneğini bankoda görünür tut; güven algısını artır.',
    'Yoğun saatlerde hızlı gösterilecek ürünleri ayrı kutu/tepsi içinde hazır beklet.',
    'Gün sonunda ilgi gören ürünleri not alıp ertesi gün vitrin ve bankoda öne taşı.'
  ], name, goal);
};

const buildStaffIdeasFallback = (campaign) => {
  const name = safeString(campaign?.name, 'Kampanya');
  const goal = safeString(campaign?.goal, 'müşteriye net fayda anlatmak');
  const savedIdeas = safeArray(campaign?.staffIdeas).filter(Boolean);
  if (savedIdeas.length) return savedIdeas.slice(0, 10);

  const text = `${name} ${goal}`.toLocaleLowerCase('tr-TR');

  if (text.includes('23 nisan') || text.includes('çocuk') || text.includes('cocuk')) {
    return [
      'Müşteriye “Çocuğunuz için günlük kullanıma uygun, minik ve zarif hediye seçeneklerimiz var” cümlesiyle yaklaş.',
      'Aile geldiğinde önce çocuğun yaşını ve ürünü günlük mü özel gün için mi düşündüklerini sor.',
      'Çocukla göz teması kurup ürünü eğlenceli anlat; “Bu bileklik sana özel küçük bir hatıra gibi durur” gibi sıcak cümle kullan.',
      'Aileye küçük bütçeli, kolay kullanılabilir ve güvenli modelleri önce göster.',
      'Hediye paketini anlatırken “23 Nisan’a özel küçük sürpriz paket gibi hazırlayabiliriz” cümlesini kullan.',
      'Kararsız aileye 3 seçenek sun: küçük küpe, mini bileklik, zarif kolye; fazla ürünle müşteriyi yorma.',
      'Çocuk ürünü seçerken rahatlık, hafiflik ve günlük kullanım bilgisini özellikle vurgula.',
      'Anne-babaya “Hatıra olarak saklanabilecek bir hediye olsun isterseniz bu modeller daha anlamlı olur” de.',
      'Satışı kapatırken “İsterseniz yanına küçük hediye notu da ekleyelim” teklifini yap.',
      'Yoğunlukta çocuklu aileleri bekletmemek için hızlı gösterim ve hızlı paketleme taktiği uygula.'
    ];
  }

  if (text.includes('anne')) {
    return [
      'Müşteriye “Annenizin tarzı daha sade mi, gösterişli mi?” sorusuyla başlayıp ürünleri buna göre daralt.',
      'Hediye arayan müşteriye önce 3 net seçenek sun: günlük kullanım, özel gün, kalıcı hatıra.',
      '“Anneler Günü’nde en çok tercih edilen modeller bunlar” diyerek karar vermeyi kolaylaştır.',
      'Müşteriye ürünün duygusal tarafını anlat: “Bu model her taktığında sizi hatırlatacak bir hediye olur.”',
      'Kararsız müşteride bütçe sorusunu yumuşak sor: “Size uygun aralıkta birkaç güzel seçenek göstereyim.”',
      'Set ürünlerde “Kolye ve küpe birlikte daha tamamlanmış bir hediye gibi durur” cümlesini kullan.',
      'Paketleme ve not kartını satışın parçası yap: “İsterseniz hediye sunumunu özel hazırlayalım.”',
      'Ürünün ayar, kullanım ve bakım bilgisini kısa anlat; teknik detaya boğma.',
      'Son karar anında aynada/tepside ürünü tekrar gösterip “hediye olarak çok zarif duruyor” diye pekiştir.',
      'Satış kapanışında sertifika, fatura ve garanti bilgisini güven veren kısa bir dille söyle.'
    ];
  }

  if (text.includes('sevgili') || text.includes('14 şubat') || text.includes('14 subat')) {
    return [
      'Müşteriye “Hediye günlük kullanım için mi, daha özel ve romantik bir şey mi olsun?” sorusuyla yaklaş.',
      'Kararsız müşteriye kalp, harf ve zarif taşlı modellerden 3 kısa alternatif sun.',
      '“Bu model hem anlamlı hem de her gün kullanılabilecek kadar sade” cümlesiyle faydayı netleştir.',
      'Çift ürünlerinde “İsterseniz uyumlu iki parça ile daha özel bir hediye hazırlayabiliriz” önerisini yap.',
      'Hediye notu ve paket seçeneğini mutlaka söyle; romantik kampanyalarda sunum satış kararını hızlandırır.',
      'Müşteri bütçeyi söylemiyorsa “Farklı fiyat aralıklarında güzel seçeneklerimiz var” diyerek rahatlat.',
      'Ürünü gösterirken “ışıltısı güçlü ama abartılı değil” gibi net ve görsel dil kullan.',
      'Son dakika müşterisine hızlı karar vereceği hazır seçenekleri göster, uzun ürün turu yaptırma.',
      'Satışı kapatırken “Paketini özel gün hediyesi gibi hazırlayalım mı?” sorusunu kullan.',
      'Müşteri tereddüt ederse değişim/garanti/sertifika bilgisini kısa ve güven verici şekilde anlat.'
    ];
  }

  if (text.includes('düğün') || text.includes('dugun') || text.includes('nişan') || text.includes('nisan')) {
    return [
      'Müşteriye “Takı merasimi için mi, gelin/damat hediyesi için mi bakıyorsunuz?” sorusuyla doğru grubu belirle.',
      'Aile alışverişlerinde önce ihtiyacı netleştir: bilezik, set, gram altın veya özel hediye.',
      '“Bu model tören için daha gösterişli, bu model günlük kullanımda daha rahat” diye iki kullanım farkını anlat.',
      'Premium ürünlerde sertifika, ayar ve işçilik bilgisini kısa ama güven veren şekilde söyle.',
      'Kalabalık gelen ailelerde tek karar verici kişiyi nazikçe belirleyip anlatımı ona odakla.',
      'Set ürünlerde “fotoğrafta ve törende daha tamamlanmış görünür” cümlesiyle görsel etkiyi vurgula.',
      'Bütçe hassasiyetinde müşteriyi sıkıştırmadan “bu aralıkta en güçlü seçenekler bunlar” diyerek yönlendir.',
      'Kararsız müşteriye en fazla 3 ürün göster; çok seçenek düğün alışverişinde kararı zorlaştırır.',
      'Paket, kutu ve sertifika sunumunu üründen sonra mutlaka göster; değer algısını yükseltir.',
      'Satışı kapatırken teslim, paketleme ve belge kontrolünü sakin ve düzenli bir dille özetle.'
    ];
  }

  return [
    `Müşteriye kampanyayı “${name} kapsamında öne çıkan avantajlı ürünlerimiz var” cümlesiyle kısa tanıt.`,
    'İlk soruda müşterinin ihtiyacını netleştir: hediye mi, günlük kullanım mı, özel gün mü?',
    'Müşteriye aynı anda çok ürün gösterme; önce 3 net seçenekle karar alanını daralt.',
    `Ürün anlatırken ${goal.toLocaleLowerCase('tr-TR')} odağını tek cümleyle müşteriye bağla.`,
    'Fiyat konuşulmadan önce ürünün kullanım alanı, hediye değeri ve görünümünü anlat.',
    'Kararsız müşteriye “sade, dikkat çekici, premium” şeklinde üç farklı yol sun.',
    'Bankoda ürünü tepsi üzerinde temiz ve düzenli göster; müşterinin ürüne odaklanmasını sağla.',
    'Müşteri tereddüt ederse garanti, sertifika ve hediye paketini kısa şekilde anlatıp güven ver.',
    'Satış kapatırken “Bunu hediye paketiyle hazırlayalım mı?” gibi net bir kapanış sorusu kullan.',
    'Gün içinde en çok sorulan ürünleri not alıp sonraki müşterilerde ilk öneri olarak kullan.'
  ];
};

const buildWebsiteIdeasFallback = (campaign) => {
  const name = safeString(campaign?.name, 'Kampanya');
  const goal = safeString(campaign?.goal, 'kampanya ilgisini artırmak');
  const savedIdeas = safeArray(campaign?.websiteIdeas).filter(Boolean);
  if (savedIdeas.length) return savedIdeas.slice(0, 10);
  return [
    `${name} için ana sayfada kampanyayı anlatan net bir banner alanı oluştur.`,
    'Kampanyalı ürünleri web sitesinde ayrı bir koleksiyon/kategori altında topla.',
    'Ürün kartlarında kampanya avantajını kısa bir etiketle göster: “Hediye önerisi”, “Sezon favorisi” gibi.',
    `Web açıklamalarında ${goal.toLocaleLowerCase('tr-TR')} mesajını ilk paragrafta sade şekilde vurgula.`,
    'Sepete yakın alanda hediye paketi, sertifika ve güven bilgilerini görünür yap.',
    'Kampanya ürünlerinde hızlı karar için en çok tercih edilen 3 ürünü üst sıraya taşı.',
    'Mobil görünümde kampanya bannerının ilk ekranda net görünmesini sağla.',
    'Ürün detayında kullanım alanı, hediye değeri ve kombin önerisini kısa bloklar halinde göster.',
    'Kampanya süresi bitmeden “son günler” uyarısı için küçük web etiketi hazırla.',
    'Web sitesinde kampanyaya özel SEO başlığı, açıklama ve etiketleri kampanya adıyla uyumlu yap.'
  ];
};

const buildInstagramIdeasFallback = (campaign) => {
  const name = safeString(campaign?.name, 'Kampanya');
  const goal = safeString(campaign?.goal, 'müşteri ilgisini artırmak');
  const savedIdeas = safeArray(campaign?.instagramIdeas).filter(Boolean);
  if (savedIdeas.length) return savedIdeas.slice(0, 10);
  return [
    `${name} için ilk gün kısa bir duyuru postu paylaş; kampanya temasını tek görselde anlat.`,
    'Story’de 3 ürünlük “hangisini seçerdiniz?” anketi yaparak etkileşim başlat.',
    'Kampanya ürünlerini mağaza içi kısa reels videosu ile göster; vitrin, banko ve paketleme detayı görünsün.',
    `Açıklama metninde ${goal.toLocaleLowerCase('tr-TR')} mesajını ilk cümlede net ver.`,
    'Her gün bir kampanya ürününü “bugünün önerisi” olarak story’de öne çıkar.',
    'Hediye paketi ve kutu sunumunu gösteren kısa video paylaş; ürünün hediye değerini artır.',
    'Müşterinin karar vermesini kolaylaştırmak için “sade / gösterişli / premium” ürün karşılaştırması paylaş.',
    'Kampanya süresince sabitlenmiş story highlight oluştur ve tüm kampanya paylaşımlarını orada topla.',
    'Son 2 gün “kampanya bitmeden” hatırlatma story’si paylaş.',
    'Mağazaya yönlendiren kısa CTA kullan: “Detaylı görmek için mağazamıza bekleriz.”'
  ];
};

function ChannelIdeasEditor({ title, description, ideas, selectedIdeas, onToggle, onSelectAll, onClearAll, channel }) {
  return (
    <Box sx={sectionSx}>
      <Typography sx={titleSx}>{title}</Typography>
      <Typography sx={{ ...textSx, color: '#5f5140', mb: 1.2 }}>{description}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onSelectAll}
          sx={{ textTransform: 'none', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.32)', fontWeight: 800 }}
        >
          Tümünü Seç
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={onClearAll}
          sx={{ textTransform: 'none', color: '#7a6b55', fontWeight: 800 }}
        >
          Tüm Seçimleri İptal Et
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {ideas.map((idea, idx) => {
          const checked = selectedIdeas.includes(idea);
          return (
            <Box
              key={`${title}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                border: '1px solid rgba(151,113,39,0.14)',
                borderRadius: 1.4,
                p: 0.95,
                bgcolor: checked ? 'rgba(151,113,39,0.1)' : 'rgba(255,255,255,0.58)'
              }}
            >
              <Checkbox
                checked={checked}
                onChange={() => onToggle(idea)}
                sx={{ color: '#8a7a63', p: 0.2, '&.Mui-checked': { color: '#166534' } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="p" sx={{ ...textSx, fontSize: '0.98rem', m: 0 }}>
                  {idea}
                  <IdeaTextCopyButton text={idea} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function NewIdeaChatEditor({
  messages,
  input,
  loading,
  error,
  onInputChange,
  onSend,
  onResetChat,
  onConfirmAdd,
  confirmAddDisabled,
  ideas,
  onRemove,
  chatScrollRef
}) {
  return (
    <Box sx={sectionSx}>
      <Typography sx={titleSx}>Yeni Fikir — AI ile sohbet</Typography>
      <Typography sx={{ ...textSx, color: '#5f5140', mb: 1.2 }}>
        Fikri asistanla konuşarak netleştirin. Uygun gördüğünüz metin için <strong>Tamam, bu oldu</strong> ile aşağıdaki listeye ekleyin; ardından pencereden{' '}
        <strong>Kaydet</strong> deyin. (AI sunucusu <code style={{ fontSize: '0.78rem' }}>openai-proxy</code> çalışıyor olmalı.)
      </Typography>

      <Box
        ref={chatScrollRef}
        sx={{
          maxHeight: { xs: 260, sm: 320 },
          overflowY: 'auto',
          mb: 1.2,
          p: 1,
          borderRadius: 1.6,
          border: '1px solid rgba(151,113,39,0.16)',
          bgcolor: 'rgba(255,252,243,0.65)',
          scrollbarWidth: 'thin'
        }}
      >
        {messages.map((m, i) => (
          <Box
            key={`chat-${i}-${m.role}`}
            sx={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 1
            }}
          >
            <Box
              sx={{
                maxWidth: '90%',
                px: 1.15,
                py: 0.85,
                borderRadius: 2,
                border: '1px solid rgba(151,113,39,0.2)',
                bgcolor: m.role === 'user' ? 'rgba(212,175,98,0.22)' : 'rgba(255,255,255,0.95)',
                boxShadow: m.role === 'user' ? 'none' : '0 2px 10px rgba(87,65,25,0.06)'
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: '#7a4d0b', mb: 0.35, letterSpacing: '0.04em' }}>
                {m.role === 'user' ? 'Siz' : 'Asistan'}
              </Typography>
              <Typography component="div" sx={{ ...textSx, m: 0, fontSize: '0.94rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {m.content}
              </Typography>
            </Box>
          </Box>
        ))}
        {loading ? (
          <Typography sx={{ color: '#7a6b55', fontSize: '0.88rem', fontStyle: 'italic', px: 0.5 }}>
            Asistan yazıyor…
          </Typography>
        ) : null}
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={5}
        size="small"
        label="Mesajınızı yazın (Enter: gönder, Shift+Enter: satır)"
        value={input}
        disabled={loading}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        sx={{
          mb: 1,
          '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.85)' },
          '& .MuiInputLabel-root': { color: '#6b5b40' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.22)' }
        }}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, justifyContent: 'flex-end', alignItems: 'center', mb: 1.2 }}>
        <Button
          size="small"
          variant="text"
          onClick={onResetChat}
          disabled={loading}
          sx={{ textTransform: 'none', color: '#7a6b55', fontWeight: 800 }}
        >
          Sohbeti sıfırla
        </Button>
        <Button
          variant="outlined"
          onClick={onSend}
          disabled={loading || !safeString(input).trim()}
          sx={{ textTransform: 'none', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.32)', fontWeight: 800 }}
        >
          {loading ? 'Gönderiliyor…' : 'Gönder'}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirmAdd}
          disabled={confirmAddDisabled}
          sx={{ textTransform: 'none', color: '#fff8e1', bgcolor: '#166534', fontWeight: 900, '&:hover': { bgcolor: '#14532d' } }}
        >
          Tamam, bu oldu — listeye ekle
        </Button>
      </Box>

      {error ? (
        <Typography sx={{ ...textSx, color: '#b91c1c', mb: 1, fontSize: '0.9rem' }}>
          {error}
        </Typography>
      ) : null}

      <Typography sx={{ ...textSx, color: '#7a4d0b', fontSize: '0.95rem', fontWeight: 900, mb: 0.7 }}>Eklenen yeni fikirler</Typography>
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {ideas.length ? (
          ideas.map((idea, idx) => (
            <Box
              key={`${idea}-${idx}`}
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'flex-start',
                border: '1px solid rgba(151,113,39,0.14)',
                borderRadius: 1.4,
                p: 0.95,
                bgcolor: 'rgba(255,255,255,0.58)'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="p" sx={{ ...textSx, m: 0, fontSize: '0.98rem' }}>
                  • {idea}
                  <IdeaTextCopyButton text={idea} />
                </Typography>
              </Box>
              <Button size="small" onClick={() => onRemove(idea)} sx={{ textTransform: 'none', color: '#b91c1c', fontWeight: 800, flexShrink: 0 }}>
                Sil
              </Button>
            </Box>
          ))
        ) : (
          <Typography sx={{ ...textSx, color: '#7a6b55' }}>Henüz sohbetten fikir eklenmedi. Yukarıda netleşen metni onaylayın.</Typography>
        )}
      </Box>
    </Box>
  );
}

function StoreIdeasEditor({ ideas, selectedIdeas, onToggle, onSelectAll, onClearAll }) {
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(ideas.length / pageSize));
  const visibleIdeas = ideas.slice(page * pageSize, page * pageSize + pageSize);

  useEffect(() => {
    setPage(0);
  }, [ideas]);

  return (
    <Box sx={sectionSx}>
      <Typography sx={titleSx}>Mağaza / Vitrin / Banko İçin AI Fikirleri</Typography>
      <Typography sx={{ ...textSx, color: '#5f5140', mb: 1.2 }}>
        Kampanyaya dahil etmek istediğiniz fikirleri işaretleyin. Her sayfada 10 fikir görünür; Kaydet’e basınca sadece işaretli fikirler kampanyada kalır.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onSelectAll}
          sx={{ textTransform: 'none', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.32)', fontWeight: 800 }}
        >
          Tümünü Seç
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={onClearAll}
          sx={{ textTransform: 'none', color: '#7a6b55', fontWeight: 800 }}
        >
          Tüm Seçimleri İptal Et
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {visibleIdeas.map((idea, idx) => {
          const checked = selectedIdeas.includes(idea);
          return (
            <Box
              key={`${idea}-${page}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                border: '1px solid rgba(151,113,39,0.14)',
                borderRadius: 1.4,
                p: 0.95,
                bgcolor: checked ? 'rgba(151,113,39,0.1)' : 'rgba(255,255,255,0.58)'
              }}
            >
              <Checkbox
                checked={checked}
                onChange={() => onToggle(idea)}
                sx={{ color: '#8a7a63', p: 0.2, '&.Mui-checked': { color: '#166534' } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="p" sx={{ ...textSx, m: 0, fontSize: '0.98rem' }}>
                  {idea}
                  <IdeaTextCopyButton text={idea} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.4 }}>
        {Array.from({ length: totalPages }).map((_, idx) => {
          const active = idx === page;
          return (
            <Button
              key={`store-idea-page-${idx}`}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              onClick={() => setPage(idx)}
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                borderColor: 'rgba(151,113,39,0.35)',
                color: active ? '#fff8e1' : '#7a4d0b',
                bgcolor: active ? '#9a6a1f' : 'rgba(255,255,255,0.72)',
                '&:hover': {
                  borderColor: '#9a6a1f',
                  bgcolor: active ? '#7a4d0b' : 'rgba(151,113,39,0.12)'
                }
              }}
            >
              {idx + 1}. Sayfa
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

function StaffIdeasEditor({ ideas, selectedIdeas, onToggle, onSelectAll, onClearAll }) {
  return (
    <Box sx={sectionSx}>
      <Typography sx={titleSx}>Personel İletişim / Satış Taktiği AI Fikirleri</Typography>
      <Typography sx={{ ...textSx, color: '#5f5140', mb: 1.2 }}>
        Kampanya boyunca personelin müşteriye nasıl yaklaşacağını, ne söyleyeceğini ve hangi satış taktiğini kullanacağını seçin.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onSelectAll}
          sx={{ textTransform: 'none', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.32)', fontWeight: 800 }}
        >
          Tümünü Seç
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={onClearAll}
          sx={{ textTransform: 'none', color: '#7a6b55', fontWeight: 800 }}
        >
          Tüm Seçimleri İptal Et
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {ideas.map((idea, idx) => {
          const checked = selectedIdeas.includes(idea);
          return (
            <Box
              key={`${idea}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                border: '1px solid rgba(151,113,39,0.14)',
                borderRadius: 1.4,
                p: 0.95,
                bgcolor: checked ? 'rgba(151,113,39,0.1)' : 'rgba(255,255,255,0.58)'
              }}
            >
              <Checkbox
                checked={checked}
                onChange={() => onToggle(idea)}
                sx={{ color: '#8a7a63', p: 0.2, '&.Mui-checked': { color: '#166534' } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="p" sx={{ ...textSx, m: 0, fontSize: '0.98rem' }}>
                  {idea}
                  <IdeaTextCopyButton text={idea} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function SubmittedIdeasEditor({ ideas, selectedIds, onToggle, onSelectAll, onClearAll }) {
  return (
    <Box sx={sectionSx}>
      <Typography sx={titleSx}>Şubelerden Sunulan Fikirler</Typography>
      <Typography sx={{ ...textSx, color: '#5f5140', mb: 1.2 }}>
        Personelin şube panelinden gönderdiği fikirleri inceleyin. İşaretlenen fikirler kampanyaya dahil olur ve şube görevlerinde görünür.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, mb: 1 }}>
        <Button size="small" variant="outlined" onClick={onSelectAll} sx={{ textTransform: 'none', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.32)', fontWeight: 800 }}>
          Tümünü Seç
        </Button>
        <Button size="small" variant="text" onClick={onClearAll} sx={{ textTransform: 'none', color: '#7a6b55', fontWeight: 800 }}>
          Tüm Seçimleri İptal Et
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {safeArray(ideas).length ? safeArray(ideas).map((idea, idx) => {
          const ideaId = safeString(idea?.id, `${idx}`);
          const checked = selectedIds.includes(ideaId);
          return (
            <Box key={ideaId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, border: '1px solid rgba(151,113,39,0.14)', borderRadius: 1.4, p: 0.95, bgcolor: checked ? 'rgba(151,113,39,0.1)' : 'rgba(255,255,255,0.58)' }}>
              <Checkbox checked={checked} onChange={() => onToggle(ideaId)} sx={{ color: '#8a7a63', p: 0.2, '&.Mui-checked': { color: '#166534' } }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#7a4d0b', fontSize: '0.82rem', fontWeight: 900 }}>
                  {safeString(idea?.branchLabel, 'Şube')} • {safeString(idea?.createdAt, '-')}
                </Typography>
                <Typography component="p" sx={{ ...textSx, fontSize: '0.98rem', m: 0, mt: 0.2 }}>
                  {safeString(idea?.text, '-')}
                  <IdeaTextCopyButton text={safeString(idea?.text, '')} />
                </Typography>
              </Box>
            </Box>
          );
        }) : (
          <Typography sx={textSx}>Henüz şubelerden sunulan fikir yok.</Typography>
        )}
      </Box>
    </Box>
  );
}

function CampaignDetailTabsModal({ open, campaign, onSave, onClose }) {
  const [tab, setTab] = useState(0);
  const storeIdeas = useMemo(() => buildStoreIdeasFallback(campaign), [campaign]);
  const staffIdeas = useMemo(() => buildStaffIdeasFallback(campaign), [campaign]);
  const websiteIdeas = useMemo(() => buildWebsiteIdeasFallback(campaign), [campaign]);
  const instagramIdeas = useMemo(() => buildInstagramIdeasFallback(campaign), [campaign]);
  const [selectedStoreIdeas, setSelectedStoreIdeas] = useState([]);
  const [selectedStaffIdeas, setSelectedStaffIdeas] = useState([]);
  const [selectedWebsiteIdeas, setSelectedWebsiteIdeas] = useState([]);
  const [selectedInstagramIdeas, setSelectedInstagramIdeas] = useState([]);
  const [customIdeaChatMessages, setCustomIdeaChatMessages] = useState([CUSTOM_IDEA_CHAT_WELCOME]);
  const [customIdeaChatInput, setCustomIdeaChatInput] = useState('');
  const [customIdeas, setCustomIdeas] = useState([]);
  const [selectedSubmittedIdeaIds, setSelectedSubmittedIdeaIds] = useState([]);
  const [customIdeaLoading, setCustomIdeaLoading] = useState(false);
  const [customIdeaError, setCustomIdeaError] = useState('');
  const customIdeaChatScrollRef = useRef(null);

  useEffect(() => {
    setSelectedStoreIdeas(campaign?.ideaSelectionsSaved ? safeArray(campaign?.storePlan).filter((item) => storeIdeas.includes(item)) : []);
  }, [campaign, storeIdeas]);

  useEffect(() => {
    setSelectedStaffIdeas(campaign?.ideaSelectionsSaved ? safeArray(campaign?.staffPlan).filter((item) => staffIdeas.includes(item)) : []);
  }, [campaign, staffIdeas]);

  useEffect(() => {
    const savedWebsitePlan = safeArray(campaign?.websitePlan).length ? safeArray(campaign?.websitePlan) : safeArray(campaign?.internetPlan);
    setSelectedWebsiteIdeas(campaign?.ideaSelectionsSaved ? savedWebsitePlan.filter((item) => websiteIdeas.includes(item)) : []);
  }, [campaign, websiteIdeas]);

  useEffect(() => {
    setSelectedInstagramIdeas(campaign?.ideaSelectionsSaved ? safeArray(campaign?.instagramPlan).filter((item) => instagramIdeas.includes(item)) : []);
  }, [campaign, instagramIdeas]);

  useEffect(() => {
    setCustomIdeas(safeArray(campaign?.customIdeas).filter(Boolean));
    setCustomIdeaChatInput('');
    setCustomIdeaChatMessages([{ ...CUSTOM_IDEA_CHAT_WELCOME }]);
    setCustomIdeaError('');
    setSelectedSubmittedIdeaIds(safeArray(campaign?.submittedIdeas).filter((idea) => idea?.approved).map((idea, index) => safeString(idea?.id, `${index}`)));
  }, [campaign]);

  const toggleStoreIdea = (idea) => {
    setSelectedStoreIdeas((prev) => (prev.includes(idea) ? prev.filter((item) => item !== idea) : [...prev, idea]));
  };
  const toggleStaffIdea = (idea) => {
    setSelectedStaffIdeas((prev) => (prev.includes(idea) ? prev.filter((item) => item !== idea) : [...prev, idea]));
  };
  const toggleWebsiteIdea = (idea) => {
    setSelectedWebsiteIdeas((prev) => (prev.includes(idea) ? prev.filter((item) => item !== idea) : [...prev, idea]));
  };
  const toggleInstagramIdea = (idea) => {
    setSelectedInstagramIdeas((prev) => (prev.includes(idea) ? prev.filter((item) => item !== idea) : [...prev, idea]));
  };
  const selectAllStoreIdeas = () => setSelectedStoreIdeas(storeIdeas);
  const clearAllStoreIdeas = () => setSelectedStoreIdeas([]);
  const selectAllStaffIdeas = () => setSelectedStaffIdeas(staffIdeas);
  const clearAllStaffIdeas = () => setSelectedStaffIdeas([]);
  const selectAllWebsiteIdeas = () => setSelectedWebsiteIdeas(websiteIdeas);
  const clearAllWebsiteIdeas = () => setSelectedWebsiteIdeas([]);
  const selectAllInstagramIdeas = () => setSelectedInstagramIdeas(instagramIdeas);
  const clearAllInstagramIdeas = () => setSelectedInstagramIdeas([]);
  const submittedIdeas = safeArray(campaign?.submittedIdeas);
  const toggleSubmittedIdea = (ideaId) => {
    setSelectedSubmittedIdeaIds((prev) => (prev.includes(ideaId) ? prev.filter((item) => item !== ideaId) : [...prev, ideaId]));
  };
  const selectAllSubmittedIdeas = () => setSelectedSubmittedIdeaIds(submittedIdeas.map((idea, index) => safeString(idea?.id, `${index}`)));
  const clearAllSubmittedIdeas = () => setSelectedSubmittedIdeaIds([]);
  const getSelectedContextIdeas = () => [
    ...selectedStoreIdeas,
    ...selectedStaffIdeas,
    ...selectedWebsiteIdeas,
    ...selectedInstagramIdeas,
    ...customIdeas
  ];

  const confirmAddDisabled = useMemo(() => {
    if (customIdeaLoading) return true;
    const hasUser = customIdeaChatMessages.some((m) => m.role === 'user');
    if (!hasUser) return true;
    const last = customIdeaChatMessages[customIdeaChatMessages.length - 1];
    if (last?.role !== 'assistant') return true;
    return String(last?.content || '').trim().length < 3;
  }, [customIdeaChatMessages, customIdeaLoading]);

  useLayoutEffect(() => {
    const el = customIdeaChatScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [customIdeaChatMessages, customIdeaLoading]);

  const handleSendCustomIdeaChat = async () => {
    const t = safeString(customIdeaChatInput).trim();
    if (!t || customIdeaLoading) return;
    setCustomIdeaError('');
    const userMsg = { role: 'user', content: t };
    const nextHistory = [...customIdeaChatMessages, userMsg];
    setCustomIdeaChatMessages(nextHistory);
    setCustomIdeaChatInput('');
    setCustomIdeaLoading(true);
    try {
      const selectedIdeas = getSelectedContextIdeas();
      const reply = await requestCustomIdeaChat({
        campaign,
        selectedIdeas,
        messages: nextHistory
      });
      setCustomIdeaChatMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setCustomIdeaError(safeString(e?.message, 'Sohbet hatası'));
    } finally {
      setCustomIdeaLoading(false);
    }
  };

  const handleResetCustomIdeaChat = () => {
    setCustomIdeaChatMessages([{ ...CUSTOM_IDEA_CHAT_WELCOME }]);
    setCustomIdeaError('');
  };

  const handleConfirmCustomIdeaFromChat = () => {
    if (confirmAddDisabled) return;
    const last = customIdeaChatMessages[customIdeaChatMessages.length - 1];
    const idea = safeString(last?.content).trim();
    if (!idea) return;
    setCustomIdeas((prev) => (prev.includes(idea) ? prev : [...prev, idea]));
    toast.info('Fikir listeye eklendi. Kalıcı kayıt için alttan Kaydet.', { autoClose: 3500 });
  };

  const handleRemoveCustomIdea = (idea) => setCustomIdeas((prev) => prev.filter((item) => item !== idea));

  const handleSave = () => {
    if (typeof onSave !== 'function') return;
    const {
      detailVisualPrompts: _dvp,
      detailVisualPromptsStatus: _dvps,
      ideaImagePrompts: _iip,
      visualPrompts: _vp,
      visualPromptsStatus: _vps,
      ...campaignRest
    } = campaign || {};
    onSave({
      ...campaignRest,
      storeIdeas,
      storePlan: selectedStoreIdeas,
      staffIdeas,
      staffPlan: selectedStaffIdeas,
      websiteIdeas,
      websitePlan: selectedWebsiteIdeas,
      internetPlan: selectedWebsiteIdeas,
      instagramIdeas,
      instagramPlan: selectedInstagramIdeas,
      customIdeas,
      submittedIdeas: submittedIdeas.map((idea, index) => {
        const ideaId = safeString(idea?.id, `${index}`);
        return { ...idea, id: ideaId, approved: selectedSubmittedIdeaIds.includes(ideaId) };
      }),
      ideaSelectionsSaved: true
    });
  };

  const detailSnapshot = useMemo(() => {
    if (!campaign) {
      return { plainText: '', printHtml: '', fileSlug: 'kampanya', panelSections: [] };
    }
    return buildCampaignDetailSnapshot(campaign, {
      details: campaign.campaignDetails || {},
      selectedStoreIdeas,
      selectedStaffIdeas,
      selectedWebsiteIdeas,
      selectedInstagramIdeas,
      customIdeas,
      submittedIdeas,
      selectedSubmittedIdeaIds
    });
  }, [
    campaign,
    selectedStoreIdeas,
    selectedStaffIdeas,
    selectedWebsiteIdeas,
    selectedInstagramIdeas,
    customIdeas,
    submittedIdeas,
    selectedSubmittedIdeaIds
  ]);

  const handlePrintDetail = () => {
    if (!detailSnapshot.printHtml) return;
    const w = window.open('', '_blank', 'width=920,height=980');
    if (!w) {
      toast.error('Tarayıcı açılır pencereyi veya yazdırmayı engelliyor olabilir.');
      return;
    }
    w.document.open();
    w.document.write(detailSnapshot.printHtml);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleDownloadDetail = () => {
    if (!detailSnapshot.plainText) return;
    const blob = new Blob([detailSnapshot.plainText], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = URL.createObjectURL(blob);
    a.download = `kampanya-${detailSnapshot.fileSlug}-${stamp}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Metin dosyası indirildi', { autoClose: 1500 });
  };

  if (!campaign) {
    return null;
  }

  const renderDetailSection = (sec) => {
    if (sec.body != null) {
      return (
        <Typography key={sec.key} sx={{ ...textSx, whiteSpace: 'pre-wrap' }}>
          {sec.body}
        </Typography>
      );
    }
    if (sec.lines) {
      return sec.lines.map((line, i) => (
        <Typography key={`${sec.key}-L${i}`} sx={textSx}>
          {line}
        </Typography>
      ));
    }
    if (Array.isArray(sec.list) && sec.list.length) {
      return (
        <Box
          component="ol"
          sx={{
            m: 0,
            pl: 2.4,
            color: textSx.color,
            fontSize: textSx.fontSize,
            lineHeight: textSx.lineHeight,
            '& li': { mb: 0.5 }
          }}
        >
          {sec.list.map((t, i) => (
            <li key={`${sec.key}-i${i}`}>
              <Typography component="span" sx={textSx}>
                {t}
              </Typography>
            </li>
          ))}
        </Box>
      );
    }
    return (
      <Typography sx={{ ...textSx, color: '#7a6b55', fontStyle: 'italic' }}>{sec.empty}</Typography>
    );
  };

  const tabPanels = [
    <Box id="campaign-detail-snapshot" sx={{ display: 'grid', gap: 1.4 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#2f2516', fontSize: '1.35rem', fontWeight: 900 }}>Kampanya Detayları</Typography>
          <Typography sx={{ color: '#6b5b40', fontSize: '0.8rem', lineHeight: 1.4, maxWidth: 520 }}>
            Bu özet; mağaza, personel, web, Instagram sekmelerinde <strong>işaretlediğiniz fikirleri</strong>, yeni fikir listesini ve
            onayladığınız şube fikirlerini anlık yansıtır. Değişiklikleri kalıcı kaydetmek için alttan <strong>Kaydet</strong> kullanın.
          </Typography>
        </Box>
        <Box sx={{ display: 'inline-flex', gap: 0.8, flexShrink: 0 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<PrintIcon fontSize="small" />}
            onClick={handlePrintDetail}
            sx={{ textTransform: 'none', fontWeight: 800, borderColor: 'rgba(151,113,39,0.4)', color: '#7a4d0b' }}
          >
            Yazdır
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon fontSize="small" />}
            onClick={handleDownloadDetail}
            sx={{ textTransform: 'none', fontWeight: 800, borderColor: 'rgba(151,113,39,0.4)', color: '#7a4d0b' }}
          >
            İndir (.txt)
          </Button>
        </Box>
      </Box>
      {detailSnapshot.panelSections.map((sec) => (
        <Box key={sec.key} sx={sectionSx}>
          <Typography sx={titleSx}>{sec.title}</Typography>
          {renderDetailSection(sec)}
        </Box>
      ))}
    </Box>,
    <StoreIdeasEditor
      ideas={storeIdeas}
      selectedIdeas={selectedStoreIdeas}
      onToggle={toggleStoreIdea}
      onSelectAll={selectAllStoreIdeas}
      onClearAll={clearAllStoreIdeas}
    />,
    <StaffIdeasEditor
      ideas={staffIdeas}
      selectedIdeas={selectedStaffIdeas}
      onToggle={toggleStaffIdea}
      onSelectAll={selectAllStaffIdeas}
      onClearAll={clearAllStaffIdeas}
    />,
    <ChannelIdeasEditor
      title="Web Sitesi İçin AI Fikirleri"
      description="Web sitesinde kampanyayı nasıl göstereceğinizi, ürünleri nasıl öne çıkaracağınızı ve satışa nasıl yönlendireceğinizi seçin."
      ideas={websiteIdeas}
      selectedIdeas={selectedWebsiteIdeas}
      onToggle={toggleWebsiteIdea}
      onSelectAll={selectAllWebsiteIdeas}
      onClearAll={clearAllWebsiteIdeas}
      channel="website"
    />,
    <ChannelIdeasEditor
      title="Instagram İçin AI Fikirleri"
      description="Instagram post, story, reels ve kampanya duyuru akışında kullanılacak fikirleri seçin."
      ideas={instagramIdeas}
      selectedIdeas={selectedInstagramIdeas}
      onToggle={toggleInstagramIdea}
      onSelectAll={selectAllInstagramIdeas}
      onClearAll={clearAllInstagramIdeas}
      channel="instagram"
    />,
    <NewIdeaChatEditor
      messages={customIdeaChatMessages}
      input={customIdeaChatInput}
      loading={customIdeaLoading}
      error={customIdeaError}
      onInputChange={setCustomIdeaChatInput}
      onSend={handleSendCustomIdeaChat}
      onResetChat={handleResetCustomIdeaChat}
      onConfirmAdd={handleConfirmCustomIdeaFromChat}
      confirmAddDisabled={confirmAddDisabled}
      ideas={customIdeas}
      onRemove={handleRemoveCustomIdea}
      chatScrollRef={customIdeaChatScrollRef}
    />,
    <SubmittedIdeasEditor
      ideas={submittedIdeas}
      selectedIds={selectedSubmittedIdeaIds}
      onToggle={toggleSubmittedIdea}
      onSelectAll={selectAllSubmittedIdeas}
      onClearAll={clearAllSubmittedIdeas}
    />
  ];

  return (
    <Dialog
      className="ky-detail-modal"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: 'ky-detail-dialog-paper',
        sx: {
          background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
          border: '1px solid rgba(151,113,39,0.24)',
          borderRadius: 2,
          color: '#2f2516',
          maxHeight: 'min(92vh, 100dvh - 12px)',
          m: { xs: 0.5, sm: 1.5 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }
      }}
    >
      <DialogTitle
        sx={{
          px: 1.6,
          py: 1.1,
          borderBottom: '1px solid rgba(151,113,39,0.14)',
          flexShrink: 0
        }}
      >
        <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: '1.35rem' }}>{safeString(campaign.name, 'Kampanya Detayı')}</Typography>
        <Typography sx={{ color: '#6b5b40', fontSize: '0.95rem' }}>{safeString(campaign.start, '-')} - {safeString(campaign.end, '-')}</Typography>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 1.2,
          borderBottom: '1px solid rgba(151,113,39,0.12)',
          flexShrink: 0,
          minHeight: 0,
          '& .MuiTab-root': { color: '#5f5140', textTransform: 'none', fontWeight: 800, fontSize: '0.98rem' },
          '& .Mui-selected': { color: '#7a4d0b !important' },
          '& .MuiTabs-indicator': { bgcolor: '#9a6a1f' }
        }}
      >
        {['Kampanya Detayları', 'Mağaza', 'Personel', 'Web Site', 'Instagram', 'Yeni Fikir', 'Sunulan Fikirler'].map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      <DialogContent
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          overflowX: 'hidden',
          /* İnce kaydırma; altyapı: Kapat / Kaydet her zaman görünür */
          scrollbarWidth: 'thin',
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(151,113,39,0.32)',
            borderRadius: 3
          },
          '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' }
        }}
      >
        {tabPanels[tab]}
      </DialogContent>
      <DialogActions
        sx={{
          px: 1.6,
          py: 1,
          borderTop: '1px solid rgba(151,113,39,0.14)',
          bgcolor: 'rgba(255,250,240,0.96)',
          flexShrink: 0
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#4b5563', fontSize: '0.95rem' }}>Kapat</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 900, bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
        >
          Kaydet
        </Button>
      </DialogActions>
      <style>{`
        @media (max-width: 600px) {
          .ky-detail-dialog-paper {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100svh !important;
            max-height: 100svh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .ky-detail-modal .MuiDialogTitle-root .MuiTypography-root:first-of-type {
            font-size: 1.05rem !important;
            word-break: break-word !important;
          }

          .ky-detail-modal .MuiInputBase-input {
            font-size: 16px !important;
          }

          .ky-detail-modal .MuiDialogActions-root {
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .ky-detail-modal .MuiDialogActions-root .MuiButton-root {
            flex: 1 1 auto !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

export default CampaignDetailTabsModal;
